/* One-time "What's Changed" splash card for the Home dashboard.
 *
 * Fires the first time a user lands on Home after the Phase 20 hub sprint.
 * Dismissal is persisted in localStorage (per-device) via a version-keyed
 * flag. Bump SHARDA_OS_VERSION to trigger a new splash for the next major
 * IA change (rather than reusing this card for unrelated notices).
 */
import React, { useState } from "react";
import { motion } from "framer-motion";
import { X, Sparkles, ArrowRight, Compass } from "lucide-react";
import { Link } from "react-router-dom";
import GlassSurface from "@/components/ui/GlassSurface";
import { Button } from "@/components/ui/button";

export const SHARDA_OS_VERSION = "2026.02.0";
const STORAGE_KEY = `whats_changed_dismissed:${SHARDA_OS_VERSION}`;

const HIGHLIGHTS = [
  { title: "5 unified hubs", desc: "Front Desk, Admin, HR, Sales, and Service now group related pages under one entry each." },
  { title: "Attendance Analytics", desc: "New CEO/HR-only 5-view analytics module with saved groups + XLSX/PDF export." },
  { title: "Notes + Meeting Log", desc: "Notion-style block editor + Meetings module with AI action items and bidirectional task sync." },
  { title: "Coming next", desc: "Unified Calendar + Cmd+K palette + Notes v2 (comments, backlinks, nested pages)." },
];

export default function WhatsChangedSplash() {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch (_e) { return false; }
  });

  if (dismissed) return null;

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch (_e) { /* localStorage blocked */ }
    setDismissed(true);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      data-testid="whats-changed-splash"
    >
      <GlassSurface variant="card" className="p-5 relative overflow-hidden">
        <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-sky-500/10 blur-3xl pointer-events-none" />
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 text-foreground/45 hover:text-foreground/80 transition"
          data-testid="whats-changed-dismiss"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-lg bg-sky-500/15 border border-sky-400/40 flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-sky-300" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/55 font-heading">What&apos;s changed · v{SHARDA_OS_VERSION}</div>
            <div className="font-heading text-lg mt-0.5">A cleaner Sharda OS is live</div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 mt-2">
          {HIGHLIGHTS.map((h, i) => (
            <div key={i} className="rounded-lg bg-white/[0.02] border border-white/[0.06] px-3 py-2.5">
              <div className="text-sm text-foreground/90">{h.title}</div>
              <div className="text-[11px] text-foreground/55 mt-0.5">{h.desc}</div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-4">
          <Button asChild size="sm" variant="outline" className="border-sky-500/40 bg-sky-500/5 text-sky-200 hover:bg-sky-500/10">
            <Link to="/tour" data-testid="whats-changed-take-tour">
              <Compass className="h-3.5 w-3.5 mr-1.5" /> Take the tour
            </Link>
          </Button>
          <Button size="sm" variant="ghost" onClick={dismiss} data-testid="whats-changed-got-it">
            Got it <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
      </GlassSurface>
    </motion.div>
  );
}
